/**
 * Shared sort helpers for live scorecard lists — table number ascending,
 * then start time ascending within the same table.
 */

/** Minimum shape required to sort a match row by table and start time. */
export interface TableSortableMatch {
  table_ids?: number[];
  start_time?: string;
  slot?: { start_time?: string };
  live_score_json?: { start_time?: string; match_id?: number };
  live_score?: boolean;
}

/** Sub-match shape used to resolve a type-2 tie board's playing table. */
export interface TieSubMatchTableSource {
  id?: number;
  meta?: { playing_table_id?: number | null };
}

/** Type-2 tie row carrying sub-matches for table resolution. */
export interface TieRowMatch extends TableSortableMatch {
  type?: number;
  sub_matches?: TieSubMatchTableSource[];
  /** Stable row key — `${parentId}_${subMatchId}` for fan-out tie boards. */
  __row_id?: string;
}

/**
 * Finds the sub-match a tie row represents — never assumes `sub_matches[0]`.
 * Resolution order: explicit `sub` arg → `live_score_json.match_id` → `__row_id` suffix.
 *
 * @param match - Tie-level row payload.
 * @param sub - Optional sub-match already identified for this row.
 * @returns The board sub-match for this row, if identifiable.
 */
export function findTieSubMatchForRow(
  match: TieRowMatch,
  sub?: TieSubMatchTableSource,
): TieSubMatchTableSource | undefined {
  if (sub) {
    return sub;
  }

  const subMatches = match.sub_matches;
  if (!Array.isArray(subMatches) || subMatches.length === 0) {
    return undefined;
  }

  const matchIdFromJson = match.live_score_json?.match_id;
  if (matchIdFromJson !== undefined) {
    const fromJson = subMatches.find((entry) => entry.id === matchIdFromJson);
    if (fromJson) {
      return fromJson;
    }
  }

  const rowId = match.__row_id;
  if (rowId) {
    const underscoreIdx = rowId.lastIndexOf('_');
    if (underscoreIdx > 0) {
      const subId = Number(rowId.slice(underscoreIdx + 1));
      if (Number.isFinite(subId)) {
        const fromRowId = subMatches.find((entry) => entry.id === subId);
        if (fromRowId) {
          return fromRowId;
        }
      }
    }
  }

  return undefined;
}

/**
 * Resolves the primary table id used for ordering; missing ids sort last.
 *
 * @param match - Match or list row carrying `table_ids`.
 * @returns Numeric table id, or `Number.MAX_SAFE_INTEGER` when absent.
 */
export function getPrimaryTableId(match: TableSortableMatch): number {
  const tableId = match.table_ids?.[0];
  return tableId !== undefined && Number.isFinite(tableId)
    ? tableId
    : Number.MAX_SAFE_INTEGER;
}

/**
 * Resolves `table_ids` for a type-2 tie row — uses the board identified by
 * `live_score_json.match_id` / `__row_id`, never `sub_matches[0]` blindly.
 * Prefers that board's `meta.playing_table_id`, then a position-aware fallback.
 *
 * @param match - Tie-level row payload.
 * @param sub - Optional sub-match already identified for this row.
 * @returns Resolved table id array, or the parent `table_ids` as fallback.
 */
export function resolveTableIdsForTieRow(
  match: TieRowMatch,
  sub?: TieSubMatchTableSource,
): number[] | undefined {
  const targetSub = findTieSubMatchForRow(match, sub);
  const playingTableId = targetSub?.meta?.playing_table_id;

  if (playingTableId != null && Number.isFinite(playingTableId)) {
    return [playingTableId];
  }

  const parentTableIds = match.table_ids;
  if (!parentTableIds?.length) {
    return undefined;
  }

  if (parentTableIds.length === 1) {
    return parentTableIds;
  }

  if (targetSub && Array.isArray(match.sub_matches)) {
    const subIndex = match.sub_matches.findIndex((entry) => entry.id === targetSub.id);
    const tableAtIndex = subIndex >= 0 ? parentTableIds[subIndex] : undefined;
    if (tableAtIndex != null && Number.isFinite(tableAtIndex)) {
      return [tableAtIndex];
    }
  }

  return parentTableIds;
}

/**
 * Parses the best available start time on a match into epoch milliseconds.
 *
 * @param match - Match or list row with optional start-time fields.
 * @returns Parsed epoch ms, or `Number.MAX_SAFE_INTEGER` when unavailable.
 */
export function getStartTimeMs(match: TableSortableMatch): number {
  const startTime =
    match.start_time ||
    match.slot?.start_time ||
    match.live_score_json?.start_time;
  const parsed = startTime ? Date.parse(startTime) : Number.NaN;
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
}

/**
 * Comparator: table ascending, then start time ascending within a table.
 *
 * @param a - First match row.
 * @param b - Second match row.
 * @returns Standard sort comparator result.
 */
export function compareByTableThenStartTime(
  a: TableSortableMatch,
  b: TableSortableMatch,
): number {
  const tableA = getPrimaryTableId(a);
  const tableB = getPrimaryTableId(b);

  if (tableA !== tableB) {
    return tableA - tableB;
  }

  return getStartTimeMs(a) - getStartTimeMs(b);
}

/**
 * Resolves live-score priority for ordering — `true` sorts before `false`.
 *
 * @param match - Match or list row carrying `live_score`.
 * @returns `0` when live scoring is active, `1` otherwise.
 */
export function getLiveScorePriority(match: TableSortableMatch): number {
  return match.live_score === true ? 0 : 1;
}

/**
 * Comparator: live_score true first, then table ascending, then start time ascending.
 *
 * @param a - First match row.
 * @param b - Second match row.
 * @returns Standard sort comparator result.
 */
export function compareByLiveScoreThenTableThenStartTime(
  a: TableSortableMatch,
  b: TableSortableMatch,
): number {
  const liveScoreA = getLiveScorePriority(a);
  const liveScoreB = getLiveScorePriority(b);

  if (liveScoreA !== liveScoreB) {
    return liveScoreA - liveScoreB;
  }

  return compareByTableThenStartTime(a, b);
}

/**
 * Sorts a match array in place by table then start time.
 *
 * @param matches - Mutable list to reorder.
 */
export function sortMatchesByTableThenStartTimeInPlace<T extends TableSortableMatch>(
  matches: T[],
): void {
  matches.sort((a, b) => compareByTableThenStartTime(a, b));
}

/**
 * Sorts a match array in place by live_score, then table, then start time.
 *
 * @param matches - Mutable list to reorder.
 */
export function sortMatchesByLiveScoreThenTableThenStartTimeInPlace<
  T extends TableSortableMatch,
>(matches: T[]): void {
  matches.sort((a, b) => compareByLiveScoreThenTableThenStartTime(a, b));
}
